// src/components/SearchResults.tsx
import { useState, useEffect } from "react";
import { Link, useSearchParams, useParams } from 'react-router';
import { useTranslation } from 'react-i18next'; 
import { Search, SlidersHorizontal, Heart, MapPin, Fuel, Gauge, Calendar, Grid3x3, List, X, Star } from 'lucide-react';
import { toast } from "sonner";
import { getPublicVehicles } from "../../../services/publicVehicle.service";
import { getPublicMakes } from "../../../services/make.service";
import { getFuelTypeDropdown } from "../../../services/fuelType.service";
import { useWishlist } from '../../context/WishlistContext';
import { Seo } from "../../components/Seo";
import { createSavedSearch } from "../../../services/savedSearch.service";

const priceRanges: Record<
  string,
  { minPrice?: number; maxPrice?: number }
> = {
  "Any Price": {},
  Under100K: { maxPrice: 100000 },
  "100K-200K": { minPrice: 100000, maxPrice: 200000 },
  "200K-400K": { minPrice: 200000, maxPrice: 400000 },
  "400K+": { minPrice: 400000 },
};

const Skeleton = ({ className = "" }: { className?: string }) => (
  <div className={`animate-pulse bg-gray-200 rounded-lg ${className}`} />
);

function SearchResultsSkeleton({
  viewMode,
}: {
  viewMode: "grid" | "list";
}) {
  return (
    <div className="min-h-screen bg-[#F5F7FA]">

      {/* Search Bar */}
      <div className="bg-white border-b border-[#E5E7EB] px-4 py-4">
        <div className="max-w-7xl mx-auto flex gap-3">

          <Skeleton className="h-12 flex-1 rounded-xl" />

          <Skeleton className="h-12 w-36 rounded-xl" />

          <Skeleton className="hidden md:block h-12 w-48 rounded-xl" />

        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">

        {/* Heading */}
        <div className="flex items-center justify-between mb-6">

          <div>
            <Skeleton className="h-7 w-44 mb-2" />
            <Skeleton className="h-4 w-64" />
          </div>

          <div className="flex gap-2">
            <Skeleton className="w-10 h-10 rounded-xl" />
            <Skeleton className="w-10 h-10 rounded-xl" />
          </div>

        </div>

        {/* GRID VIEW */}
        {viewMode === "grid" ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">

            {[...Array(8)].map((_, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl border border-[#E5E7EB] overflow-hidden"
              >
                <Skeleton className="h-48 w-full rounded-none" />

                <div className="p-4">

                  <Skeleton className="h-5 w-3/4 mb-3" />

                  <Skeleton className="h-6 w-28 mb-4" />

                  <div className="space-y-2 mb-4">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-5/6" />
                  </div>

                  <div className="flex justify-between items-center">

                    <Skeleton className="h-6 w-20 rounded-lg" />

                    <Skeleton className="h-9 w-20 rounded-lg" />

                  </div>

                </div>
              </div>
            ))}

          </div>
        ) : (
          /* LIST VIEW */
          <div className="space-y-4">

            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl border border-[#E5E7EB] overflow-hidden flex"
              >
                <Skeleton className="w-60 h-56 rounded-none flex-shrink-0" />

                <div className="flex-1 p-5">

                  <div className="flex justify-between mb-3">
                    <Skeleton className="h-6 w-64" />
                    <Skeleton className="w-8 h-8 rounded-lg" />
                  </div>

                  <Skeleton className="h-7 w-32 mb-4" />

                  <div className="space-y-2 mb-5">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-5/6" />
                  </div>

                  <div className="flex justify-between items-center">
                    <Skeleton className="h-6 w-24 rounded-lg" />
                    <Skeleton className="h-10 w-32 rounded-xl" />
                  </div>

                </div>
              </div>
            ))}

          </div>
        )}

        {/* Pagination */}
        <div className="flex justify-center gap-2 mt-10">

          {[...Array(7)].map((_, i) => (
            <Skeleton
              key={i}
              className="w-10 h-10 rounded-xl"
            />
          ))}

        </div>

      </div>
    </div>
  );
}

const VEHICLES_PER_PAGE = 10;

export function SearchResults() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { makeId, brandId } = useParams<{ makeId?: string; brandId?: string }>();
  const { i18n, t } = useTranslation('auth'); 
  const { toggleWishlist, isInWishlist, canUseWishlist } = useWishlist();
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [filterOpen, setFilterOpen] = useState(false);
  const [sortBy, setSortBy] = useState('newest');
  const urlMake = searchParams.get('make') || 'All Makes';
  const [filters, setFilters] = useState({ make: urlMake, fuel: 'All', price: 'Any Price', year: '', km: '' });
  const urlQuery = searchParams.get('search') || searchParams.get('q') || '';
  const [query, setQuery] = useState(urlQuery);
  const [loading, setLoading] = useState(true);
  const [vehicles, setVehicles] = useState<any[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [breadcrumbNames, setBreadcrumbNames] = useState({makeName: '', brandName: '',});
  const [savingSearch, setSavingSearch] = useState(false);
  const homepageFilters = {
    make: searchParams.get('make') || '',
    category: searchParams.get('category') || '',
    bodyType: searchParams.get('bodyType') || '',
    city: searchParams.get('city') || '',
    minPrice: searchParams.get('minPrice') || '',
    maxPrice: searchParams.get('maxPrice') || '',
  };

  // Dropdown data
  const [makes, setMakes] = useState<{ ID: string; MakeName: string }[]>([]);
  const [fuelTypes, setFuelTypes] = useState<{ ID: string; FuelTypeName: string }[]>([]);

  // Keep this page in sync when a new navbar search or homepage search updates the URL
  useEffect(() => {
    setQuery(urlQuery);
    const paramMake = searchParams.get('make');
    if (paramMake) {
      setFilters(prev => ({ ...prev, make: paramMake }));
    }
    setCurrentPage(1);
  }, [urlQuery, searchParams]);

  // Fetch dropdown options
  useEffect(() => {
    const loadDropdowns = async () => {
      try {
        const makesData = await getPublicMakes();
        setMakes(makesData || []);
        const fuelsData = await getFuelTypeDropdown();
        setFuelTypes(fuelsData || []);
      } catch (error) {
        console.error("Failed to load dropdowns", error);
      }
    };
    loadDropdowns();
  }, []);
  useEffect(() => {
    if (makeId && makes.length > 0) {
      const m = makes.find((x: any) => x.ID === makeId);
      setBreadcrumbNames((prev) => ({ ...prev, makeName: m?.MakeName || '' }));
    }
  }, [makeId, makes]);

  // Derive breadcrumb names from fetched vehicles (public data, no extra API call)
useEffect(() => {
  if (vehicles.length > 0) {
    const first = vehicles[0];
    setBreadcrumbNames((prev) => ({
      makeName: prev.makeName || first.MakeName || '',
      brandName: first.BrandName || prev.brandName || '',
    }));
  }
}, [vehicles]);


  

  // Fetch vehicles with current filters
  const fetchVehicles = async () => {
    try {
      setLoading(true);
      
      // The option values are display-friendly; map them to actual MAD amounts.
      const { minPrice, maxPrice } = priceRanges[filters.price] || {};

      // Map sortBy
      let sortByField = 'CreatedOn';
      let sortOrder = 'DESC';
      if (sortBy === 'price_asc') {
        sortByField = 'Price';
        sortOrder = 'ASC';
      } else if (sortBy === 'price_desc') {
        sortByField = 'Price';
        sortOrder = 'DESC';
      } else if (sortBy === 'km') {
        sortByField = 'Mileage';
        sortOrder = 'ASC';
      } else if (sortBy === 'newest') {
        sortByField = 'CreatedOn';
        sortOrder = 'DESC';
      }

      const activeMake = filters.make !== 'All Makes' ? filters.make : (searchParams.get('make') || '');

      const params = {
        page: currentPage,
        pageSize: VEHICLES_PER_PAGE,
        sortBy: sortByField,
        sortOrder,
        search: query,
        make: activeMake,
        makeId: makeId || '',         
        brandId: brandId || '',
        fuel: filters.fuel !== 'All' ? filters.fuel : '',
        minPrice: homepageFilters.minPrice ? Number(homepageFilters.minPrice) : minPrice,
        maxPrice: homepageFilters.maxPrice ? Number(homepageFilters.maxPrice) : maxPrice,
        year: filters.year,
        maxKm: filters.km,
        city: homepageFilters.city,
        bodyType: homepageFilters.bodyType,
        category: homepageFilters.category,
      };

      const response = await getPublicVehicles(params);
      if (response.success) {
        setVehicles(response.Data);
        setTotalRecords(response.pagination.total_records);
        setTotalPages(response.pagination.total_pages);
      } else {
        toast.error(response.message || "Failed to load vehicles");
      }
    } catch (error: any) {
      toast.error(error?.message || "Failed to load vehicles");
    } finally {
      setLoading(false);
    }
  };

  // Fetch on mount and when dependencies change
  useEffect(() => {
    fetchVehicles();
  }, [currentPage, query, sortBy, filters, searchParams, i18n.language, makeId, brandId]);

  const applyFilters = () => {
    setCurrentPage(1);
    fetchVehicles();
    setFilterOpen(false);
  };

  const clearFilters = () => {
    setFilters({ make: 'All Makes', fuel: 'All', price: 'Any Price', year: '', km: '' });
    setQuery('');
    setCurrentPage(1);
    setSearchParams({});
  };

  const saveCurrentSearch = async () => {
  if (savingSearch) return;

  setSavingSearch(true);

  try {
    // Resolve dropdown labels to their actual database IDs.
    const selectedMake = makes.find(
      (item) =>
        item.MakeName.toLowerCase() === filters.make.toLowerCase()
    );

    const selectedFuel = fuelTypes.find(
      (item) =>
        item.FuelTypeName.toLowerCase() === filters.fuel.toLowerCase()
    );

    const isUuid = (value?: string | null): boolean =>
      Boolean(
        value &&
        /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value)
      );

    const parseOptionalNumber = (
      value: string
    ): number | undefined => {
      if (!value.trim()) return undefined;

      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : undefined;
    };

    const selectedRange = priceRanges[filters.price] || {};

    const urlMinPrice = parseOptionalNumber(homepageFilters.minPrice);
    const urlMaxPrice = parseOptionalNumber(homepageFilters.maxPrice);

    const minPrice = urlMinPrice ?? selectedRange.minPrice;
    const maxPrice = urlMaxPrice ?? selectedRange.maxPrice;

    const minYear = parseOptionalNumber(filters.year);
    const maxMileage = parseOptionalNumber(filters.km);

    if (minYear !== undefined && !Number.isInteger(minYear)) {
      toast.error("Minimum year must be a whole number.");
      return;
    }

    if (
      (minPrice !== undefined && minPrice < 0) ||
      (maxPrice !== undefined && maxPrice < 0) ||
      (maxMileage !== undefined && maxMileage < 0)
    ) {
      toast.error("Price and mileage cannot be negative.");
      return;
    }

    if (
      minPrice !== undefined &&
      maxPrice !== undefined &&
      minPrice > maxPrice
    ) {
      toast.error("Minimum price cannot exceed maximum price.");
      return;
    }

    const searchNameParts: string[] = [];

    if (filters.make !== "All Makes") {
      searchNameParts.push(filters.make);
    }

    if (filters.fuel !== "All") {
      searchNameParts.push(filters.fuel);
    }

    if (filters.price !== "Any Price") {
      searchNameParts.push(filters.price);
    }

    if (filters.year.trim()) {
      searchNameParts.push(`From ${filters.year}`);
    }

    if (filters.km.trim()) {
      searchNameParts.push(`Up to ${filters.km} km`);
    }

    const bodyTypeId = isUuid(homepageFilters.bodyType)
      ? homepageFilters.bodyType
      : null;

    const cityId = isUuid(homepageFilters.city)
      ? homepageFilters.city
      : null;

    // These URL filters are display values in some routes.
    // The API expects UUIDs, so never send names as IDs.
    if (
      (homepageFilters.bodyType && !bodyTypeId) ||
      (homepageFilters.city && !cityId)
    ) {
      toast.info(
        "Body type or city may need ID mapping before it can be saved."
      );
    }

    const payload = {
      SearchName: searchNameParts.length
        ? searchNameParts.join(" · ").slice(0, 150)
        : "My saved search",

      MakeID:
        selectedMake?.ID ||
        (isUuid(makeId) ? makeId : null),

      BrandID: isUuid(brandId) ? brandId : null,
      ModelID: null,

      FuelTypeID: selectedFuel?.ID || null,
      BodyTypeID: bodyTypeId,
      CityID: cityId,

      PriceMin: minPrice ?? null,
      PriceMax: maxPrice ?? null,

      YearMin: minYear ?? null,
      YearMax: null,

      MileageMin: null,
      MileageMax: maxMileage ?? null,

      ColorID: null,
      CountryID: null,
      StateID: null,

      TransmissionType: null,
      Condition: null,
      DriveType: null,
      PurchaseType: null,
      IsFirstOwner: null,

      FeatureIDs: [],

      NotifyEmail: true,
      NotifyInApp: true,
      IsActive: true,
    };

    const response = await createSavedSearch(payload);

    if (response?.success) {
      toast.success("Search saved! You'll receive matching vehicle alerts.");
    } else {
      toast.error(response?.message || "Could not save this search.");
    }
  } catch (error: any) {
    toast.error(
      error?.response?.data?.message ||
        "Unable to save search. Please log in as a buyer and try again."
    );
  } finally {
    setSavingSearch(false);
  }
};

  const badgeColors: Record<string, string> = {
    'Featured': 'bg-[#1E6DFF] text-white',
    'New': 'bg-[#22C55E] text-white',
    'Hot Deal': 'bg-[#EF4444] text-white',
  };

  const VehicleCard = ({ v }: { v: any }) => {
    const title = v.Title || `${v.MakeName || ''} ${v.ModelName || ''} ${v.YearOfRegistration || ''}`.trim() || 'Vehicle';
    const image = v.Images?.[0] || 'https://images.unsplash.com/photo-1617469767-1c6e04aaed8f?w=400&h=260&fit=crop';
    const price = v.Price ? `${Number(v.Price).toLocaleString()} MAD` : t('vehicle.price_on_request');
    const isSaved = isInWishlist(v.ID);

    const handleToggleWishlist = () => {
      toggleWishlist({
        id: v.ID,
        title,
        price: v.Price ? Number(v.Price) : price,
        location: v.CityName || v.CityID || 'Morocco',
        year: v.YearOfRegistration,
        km: v.Mileage ? Number(v.Mileage) : undefined,
        fuel: v.FuelTypeName || v.FuelType,
        img: image
      });
    };

    // Get badge translation
    const getBadgeText = (badge: string) => {
      const badgeMap: Record<string, string> = {
        'Featured': t('badges.featured'),
        'New': t('badges.new'),
        'Hot Deal': t('badges.hot_deal'),
      };
      return badgeMap[badge] || badge;
    };

    return (
      viewMode === 'grid' ? (
        <div className="bg-white rounded-2xl border border-[#E5E7EB] overflow-hidden hover:shadow-lg transition-shadow group">
          <div className="relative overflow-hidden">
            <img src={image} alt={title} className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500" />
            {canUseWishlist && <button onClick={handleToggleWishlist} className="absolute top-3 right-3 p-2 bg-white/90 backdrop-blur rounded-lg shadow hover:bg-white transition-colors z-10" title={isSaved ? t('searchVehicle.vehicle.wishlist_remove') : t('searchVehicle.vehicle.wishlist_add')}>
              <Heart className={`w-4 h-4 ${isSaved ? 'fill-[#EF4444] text-[#EF4444]' : 'text-[#717182]'}`} />
            </button>}
            {v.badge && <span className={`absolute top-3 left-3 px-2 py-1 rounded-lg text-xs ${badgeColors[v.badge]}`}>{getBadgeText(v.badge)}</span>}
          </div>
          <div className="p-4">
            <div className="flex items-start justify-between mb-2">
              <h3 className="text-[#2D323A] leading-tight font-semibold truncate">{title}</h3>
            </div>
            <p className="text-[#1E6DFF] font-semibold mb-3">{price}</p>
            <div className="flex flex-wrap gap-2 mb-3 text-xs text-[#717182]">
              <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{v.YearOfRegistration}</span>
              <span className="flex items-center gap-1"><Gauge className="w-3 h-3" />{v.Mileage ? Number(v.Mileage).toLocaleString() : 'N/A'} km</span>
              <span className="flex items-center gap-1"><Fuel className="w-3 h-3" />{v.FuelType || 'N/A'}</span>
              <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{v.CityName || v.CityID || 'N/A'}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1">
                <span className={`text-xs px-2 py-0.5 rounded-lg ${v.SellerTypeLabel === 'Professional' ? 'bg-[#1E6DFF]/10 text-[#1E6DFF]' : 'bg-[#F5F7FA] text-[#717182]'}`}>
                  {v.SellerTypeLabel === 'Professional' ? t('searchVehicle.vehicle.professional') : t('searchVehicle.vehicle.seller')}
                </span>
                {v.rating && <span className="flex items-center gap-0.5 text-xs text-[#F59E0B]"><Star className="w-3 h-3 fill-current" />{v.rating}</span>}
              </div>
              <Link to={`/vehicle/${v.ID}`} className="text-xs px-3 py-1.5 bg-[#1E6DFF] text-white rounded-lg hover:bg-[#1557d9] transition-colors font-medium">
                {t('searchVehicle.vehicle.view')}
              </Link>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-[#E5E7EB] overflow-hidden hover:shadow-lg transition-shadow flex">
          <div className="relative w-60 flex-shrink-0 overflow-hidden">
            <img src={image} alt={title} className="w-full h-full object-cover" />
            {v.badge && <span className={`absolute top-3 left-3 px-2 py-1 rounded-lg text-xs ${badgeColors[v.badge]}`}>{getBadgeText(v.badge)}</span>}
          </div>
          <div className="flex-1 p-5 flex flex-col justify-between">
            <div>
              <div className="flex items-start justify-between mb-1">
                <h3 className="text-[#2D323A] font-semibold">{title}</h3>
                {canUseWishlist && <button onClick={handleToggleWishlist} className="p-2 hover:bg-[#F5F7FA] rounded-lg transition-colors" title={isSaved ? t('searchVehicle.vehicle.wishlist_remove') : t('searchVehicle.vehicle.wishlist_add')}>
                  <Heart className={`w-4 h-4 ${isSaved ? 'fill-[#EF4444] text-[#EF4444]' : 'text-[#717182]'}`} />
                </button>}
              </div>
              <p className="text-xl text-[#1E6DFF] font-semibold mb-3">{price}</p>
              <div className="flex flex-wrap gap-3 text-sm text-[#717182]">
                <span className="flex items-center gap-1"><Calendar className="w-4 h-4" />{v.YearOfRegistration}</span>
                <span className="flex items-center gap-1"><Gauge className="w-4 h-4" />{v.Mileage ? Number(v.Mileage).toLocaleString() : 'N/A'} km</span>
                <span className="flex items-center gap-1"><Fuel className="w-4 h-4" />{v.FuelType || 'N/A'}</span>
                <span className="flex items-center gap-1"><MapPin className="w-4 h-4" />{v.CityName || v.CityID || 'N/A'}</span>
              </div>
            </div>
            <div className="flex items-center justify-between mt-3">
              <span className={`text-xs px-2 py-1 rounded-lg ${v.SellerTypeLabel === 'Professional' ? 'bg-[#1E6DFF]/10 text-[#1E6DFF]' : 'bg-[#F5F7FA] text-[#717182]'}`}>
                {v.SellerTypeLabel === 'Professional' ? t('vehicle.professional') : t('searchVehicle.vehicle.seller')}
              </span>
              <Link to={`/vehicle/${v.ID}`} className="px-4 py-2 bg-[#1E6DFF] text-white rounded-xl hover:bg-[#1557d9] transition-colors text-sm font-medium">
                {t('searchVehicle.vehicle.view_details')}
              </Link>
            </div>
          </div>
        </div>
      )
    );
  };

  if (loading && vehicles.length === 0) {
    return <SearchResultsSkeleton viewMode={viewMode} />;
  }

  return (
    <>
    <Seo
  title={t('searchVehicle.seo.title')}
  description={t('searchVehicle.seo.description')}
/>
    <div className="min-h-screen bg-[#F5F7FA]">
      {/* Search Bar */}
      <div className="bg-white border-b border-[#E5E7EB] px-4 py-4">
        <div className="max-w-7xl mx-auto flex gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#717182]" />
            <input
              type="text"
              value={query}
              onChange={e => { setQuery(e.target.value); setCurrentPage(1); }}
              placeholder={t('searchVehicle.search.placeholder')}
              className="w-full pl-12 pr-4 py-3 bg-[#F5F7FA] border border-[#E5E7EB] rounded-xl focus:outline-none focus:border-[#1E6DFF]"
            />
          </div>
          <button
            onClick={() => setFilterOpen(!filterOpen)}
            className="flex items-center gap-2 px-4 py-3 border border-[#E5E7EB] bg-white rounded-xl hover:border-[#1E6DFF] transition-colors text-[#2D323A]"
          >
            <SlidersHorizontal className="w-5 h-5" />
            <span className="hidden sm:block">{t('searchVehicle.search.filters')}</span>
          </button>
          <select
            value={sortBy}
            onChange={e => { setSortBy(e.target.value); setCurrentPage(1); }}
            className="hidden md:block px-4 py-3 border border-[#E5E7EB] bg-white rounded-xl focus:outline-none focus:border-[#1E6DFF] text-[#2D323A]"
          >
            <option value="newest">{t('searchVehicle.search.sort.newest')}</option>
            <option value="price_asc">{t('searchVehicle.search.sort.price_asc')}</option>
            <option value="price_desc">{t('searchVehicle.search.sort.price_desc')}</option>
            <option value="km">{t('searchVehicle.search.sort.km')}</option>
          </select>
        </div>
      </div>
      {/* Breadcrumb — only when on Make/Brand pages */}
      {(makeId || brandId) && (
        <div className="bg-white border-b border-[#E5E7EB] px-4 py-3">
          <div className="max-w-7xl mx-auto">
            <nav className="flex items-center gap-2 text-sm text-[#717182] flex-wrap">
              <Link to="/" className="hover:text-[#1E6DFF]">
                {t('vehicleDetail.breadcrumb.home')}
              </Link>
              <span>/</span>
              <Link to="/search" className="hover:text-[#1E6DFF]">
                {t('vehicleDetail.breadcrumb.vehicles')}
              </Link>

              {makeId && (
                <>
                  <span>/</span>
                  {brandId ? (
                    <Link
                      to={`/vehicles/make/${makeId}`}
                      className="hover:text-[#1E6DFF] capitalize"
                    >
                      {breadcrumbNames.makeName || 'Make'}
                    </Link>
                  ) : (
                    <span className="text-[#2D323A] capitalize">
                      {breadcrumbNames.makeName || 'Make'}
                    </span>
                  )}
                </>
              )}

              {brandId && (
                <>
                  <span>/</span>
                  <span className="text-[#2D323A] capitalize">
                    {breadcrumbNames.brandName || 'Brand'}
                  </span>
                </>
              )}
            </nav>
          </div>
        </div>
      )}

      {/* Filter Drawer */}
      {filterOpen && (
        <div className="bg-white border-b border-[#E5E7EB] px-4 py-5">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-[#2D323A]">{t('searchVehicle.filters.title')}</h3>
              <button onClick={() => setFilterOpen(false)} className="p-1 hover:bg-[#F5F7FA] rounded-lg"><X className="w-5 h-5 text-[#717182]" /></button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
              {/* Make */}
              <div>
                <label className="block text-xs text-[#717182] mb-1">{t('searchVehicle.filters.make')}</label>
                <select
                  value={filters.make}
                  onChange={e => setFilters({ ...filters, make: e.target.value })}
                  className="w-full px-3 py-2 border border-[#E5E7EB] rounded-xl text-sm focus:outline-none focus:border-[#1E6DFF] bg-[#F5F7FA]"
                >
                  <option value="All Makes">{t('searchVehicle.filters.all_makes')}</option>
                  {makes.map(m => <option key={m.ID} value={m.MakeName}>{m.MakeName}</option>)}
                </select>
              </div>
              {/* Fuel */}
              <div>
                <label className="block text-xs text-[#717182] mb-1">{t('searchVehicle.filters.fuel')}</label>
                <select
                  value={filters.fuel}
                  onChange={e => setFilters({ ...filters, fuel: e.target.value })}
                  className="w-full px-3 py-2 border border-[#E5E7EB] rounded-xl text-sm focus:outline-none focus:border-[#1E6DFF] bg-[#F5F7FA]"
                >
                  <option value="All">{t('searchVehicle.filters.all_fuel')}</option>
                  {fuelTypes.map(f => <option key={f.ID} value={f.FuelTypeName}>{f.FuelTypeName}</option>)}
                </select>
              </div>
              {/* Price */}
              <div>
                <label className="block text-xs text-[#717182] mb-1">{t('searchVehicle.filters.price')}</label>
                <select
                  value={filters.price}
                  onChange={e => setFilters({ ...filters, price: e.target.value })}
                  className="w-full px-3 py-2 border border-[#E5E7EB] rounded-xl text-sm focus:outline-none focus:border-[#1E6DFF] bg-[#F5F7FA]"
                >
                  <option value="Any Price">{t('searchVehicle.filters.any_price')}</option>
                  <option value="Under100K">{t('searchVehicle.filters.under_100k')}</option>
                  <option value="100K-200K">{t('searchVehicle.filters.100k_200k')}</option>
                  <option value="200K-400K">{t('searchVehicle.filters.200k_400k')}</option>
                  <option value="400K+">{t('searchVehicle.filters.400k_plus')}</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-[#717182] mb-1">{t('searchVehicle.filters.min_year')}</label>
                <input
                  type="number"
                  placeholder="2015"
                  value={filters.year}
                  onChange={e => setFilters({ ...filters, year: e.target.value })}
                  className="w-full px-3 py-2 border border-[#E5E7EB] rounded-xl text-sm focus:outline-none focus:border-[#1E6DFF] bg-[#F5F7FA]"
                />
              </div>
              <div>
                <label className="block text-xs text-[#717182] mb-1">{t('searchVehicle.filters.max_km')}</label>
                <input
                  type="number"
                  placeholder="100000"
                  value={filters.km}
                  onChange={e => setFilters({ ...filters, km: e.target.value })}
                  className="w-full px-3 py-2 border border-[#E5E7EB] rounded-xl text-sm focus:outline-none focus:border-[#1E6DFF] bg-[#F5F7FA]"
                />
              </div>
              <div className="flex items-end gap-2">
                <button
                  onClick={applyFilters}
                  className="flex-1 py-2 bg-[#1E6DFF] text-white rounded-xl hover:bg-[#1557d9] transition-colors text-sm"
                >
                  {t('searchVehicle.filters.apply')}
                </button>
                <button
                  onClick={clearFilters}
                  className="px-3 py-2 border border-[#E5E7EB] rounded-xl hover:bg-[#F5F7FA] text-sm text-[#717182]"
                >
                  {t('searchVehicle.filters.clear')}
                </button>
              </div>
              
<div className="flex items-end">
  <button
    type="button"
    onClick={saveCurrentSearch}
    disabled={savingSearch}
    className="flex-1 py-2 bg-[#1E6DFF] text-white rounded-xl hover:bg-[#1557d9] transition-colors text-sm"
  >
    {savingSearch ? "Saving..." : "Save & Get Alerts"}
  </button>
</div>

            </div>
                        {/* Save Search Button */}
            
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            {/* <h2 className="text-[#2D323A]">{totalRecords} {t('searchVehicle.title')}</h2> */}
            <h2 className="text-[#2D323A]">
              {totalRecords}{' '}
              {makeId && breadcrumbNames.makeName
                ? `${breadcrumbNames.makeName}${
                    brandId && breadcrumbNames.brandName
                      ? ' ' + breadcrumbNames.brandName
                      : ''
                  } Vehicles`
                : t('searchVehicle.title')}
            </h2>
            <p className="text-sm text-[#717182]">{t('searchVehicle.subtitle')}</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-xl transition-colors ${viewMode === 'grid' ? 'bg-[#1E6DFF] text-white' : 'bg-white border border-[#E5E7EB] text-[#717182]'}`}
              title={t('searchVehicle.view.grid')}
            >
              <Grid3x3 className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`hidden sm:block p-2 rounded-xl transition-colors ${viewMode === 'list' ? 'bg-[#1E6DFF] text-white' : 'bg-white border border-[#E5E7EB] text-[#717182]'}`}
              title={t('searchVehicle.view.list')}
            >
              <List className="w-5 h-5" />
            </button>
          </div>
        </div>

        {loading && vehicles.length === 0 ? (
          <SearchResultsSkeleton viewMode={viewMode} />
        ) : vehicles.length === 0 ? (
          <div className="text-center py-10 text-[#717182]">{t('searchVehicle.no_results')}</div>
        ) : (
          <div className={viewMode === 'grid' ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5' : 'space-y-4'}>
            {vehicles.map(v => <VehicleCard key={v.ID} v={v} />)}
          </div>
        )}

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-10">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
              <button
                key={p}
                onClick={() => setCurrentPage(p)}
                className={`w-10 h-10 rounded-xl text-sm transition-colors ${p === currentPage ? 'bg-[#1E6DFF] text-white' : 'bg-white border border-[#E5E7EB] text-[#717182] hover:border-[#1E6DFF]'}`}
              >
                {p}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
    </>
  );
}